"use client";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

interface ToastContextType {
  showToast: (
    message: string,
    type?: "success" | "danger" | "info" | "warning",
    timeout?: number
  ) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a CustomToastProvider");
  }
  return context;
};

let toastInstance: ToastContextType | null = null;

export const initializeToast = (instance: ToastContextType) => {
  toastInstance = instance;
};

export const showToast = (
  message: any,
  type: "success" | "danger" | "info" | "warning" = "info",
  timeout?: number
) => {
  if (toastInstance) {
    toastInstance.showToast(message, type, timeout);
  }
};

interface CustomToastProviderProps {
  children: ReactNode;
}

export const CustomToastProvider: React.FC<CustomToastProviderProps> = ({
  children,
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toggle = () => setIsVisible(!isVisible);
  const [mounted, setMounted] = useState<boolean>(false);
  const [toastClass, setToastClass] = useState<string>("");
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [message, setMessage] = useState<any>("");
  const [toastType, setToastType] = useState<
    "success" | "error" | "danger" | "info" | "warning"
  >();

  const showToastInternal = (message: any, type: any, timeout?: number) => {
    // console.log("showToastInternal", message, type);
    if(message){
      setMessage(message);
    }
   
    setToastType(type == "danger" ? "error" : type);
    setIsVisible(true);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Auto-hide
    if (timeout !== undefined) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, timeout);
    }

    // setTimeout(() => setIsVisible(false), 3000);
  };

  useEffect(() => {
    setMounted(true);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  
  const contextValue = { showToast: showToastInternal };

  initializeToast(contextValue);

  useEffect(() => {
    if (toastType) {
      setToastClass(toastType == "error" ? "text-red-2" : "text-green-2");
    }
  }, [toastType]);
  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {mounted && (
        <div style={{ position: "fixed", top: "50%", left: "50%", zIndex: 9999 }}>
          <Modal isOpen={isVisible} toggle={toggle} fade={false}>
            <ModalHeader toggle={toggle} className={toastClass}>
              {toastType?.toUpperCase()}
            </ModalHeader>
            <ModalBody className={toastClass}>{message ? message : ""}</ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                onClick={toggle}
                className="button -dark-1 bg-blue-1 text-white w-30"
              >
                Close
              </Button>
            </ModalFooter>
          </Modal>
        </div>
      )}
    </ToastContext.Provider>
  );
};
